import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLiveQuery } from "dexie-react-hooks";

import { db, type OutboxFieldNote, type OutboxPunch } from "@/lib/db";
import { requestSync } from "@/lib/syncScheduler";
import { SYNC_CHANNEL_NAME, type SyncMessage } from "@/lib/syncEngine";
import { useStorageCapability } from "@/hooks/useStorageCapability";

export type OutboxState = {
  /** The user's not-yet-synced punches in capture (FIFO) order. */
  queued: OutboxPunch[];
  /** The user's most recent local punch regardless of sync status — the freshest
   * signal of their intended clock state while server queries catch up. */
  lastPunch: OutboxPunch | null;
  /** Standalone field notes pending in the outbox (for the "New Field Note" form). */
  fieldNoteQueued: OutboxFieldNote[];
  pendingCount: number; // punches + field notes
  syncing: boolean;
  authRequired: boolean;
  failed: OutboxPunch[];
  fieldNoteFailed: OutboxFieldNote[];
  lastError: { status: number | null; message: string } | null;
  syncNow: () => void;
  /** True when browser storage is degraded (Private mode / no durable IDB). */
  storageDegraded: boolean;
};

const EMPTY: Omit<OutboxState, "syncNow" | "storageDegraded"> = {
  queued: [],
  lastPunch: null,
  fieldNoteQueued: [],
  pendingCount: 0,
  syncing: false,
  authRequired: false,
  failed: [],
  fieldNoteFailed: [],
  lastError: null,
};

export function useOutbox(userId: number | undefined): OutboxState {
  const queryClient = useQueryClient();
  const { degraded } = useStorageCapability();

  // When degraded, queriers must not touch Dexie (Safari Private crashes on IDB).
  const rows =
    useLiveQuery(
      async () => {
        if (degraded || userId === undefined) return [] as OutboxPunch[];
        return db.outbox.where("userId").equals(userId).sortBy("id");
      },
      [userId, degraded],
      [] as OutboxPunch[],
    ) ?? [];

  const noteRows =
    useLiveQuery(
      async () => {
        if (degraded || userId === undefined) return [] as OutboxFieldNote[];
        return db.fieldNotesOutbox.where("userId").equals(userId).sortBy("id");
      },
      [userId, degraded],
      [] as OutboxFieldNote[],
    ) ?? [];

  // Refresh server-derived views in every tab when a punch or field note lands.
  React.useEffect(() => {
    if (degraded) return;
    let channel: BroadcastChannel | null = null;
    try {
      channel = new BroadcastChannel(SYNC_CHANNEL_NAME);
      channel.onmessage = (event: MessageEvent<SyncMessage>) => {
        if (event.data?.type === "synced") {
          void queryClient.invalidateQueries({ queryKey: ["entries"] });
          void queryClient.invalidateQueries({ queryKey: ["recent-entries"] });
          void queryClient.invalidateQueries({ queryKey: ["entries-count"] });
          void queryClient.invalidateQueries({ queryKey: ["recent-entries-count"] });
          // Field note success also refreshes the notes lists.
          void queryClient.invalidateQueries({ queryKey: ["field-notes"] });
          void queryClient.invalidateQueries({ queryKey: ["field-notes-count"] });
          void queryClient.invalidateQueries({ queryKey: ["field-notes-review"] });
          void queryClient.invalidateQueries({ queryKey: ["punch-notes-review"] });
          void queryClient.invalidateQueries({ queryKey: ["punch-notes-review-count"] });
        }
      };
    } catch {
      // BroadcastChannel unsupported — Dexie live queries still keep the badge fresh.
    }
    return () => channel?.close();
  }, [queryClient, degraded]);

  if (degraded) {
    return {
      ...EMPTY,
      storageDegraded: true,
      syncNow: () => {
        /* no outbox to drain */
      },
    };
  }

  const queued = rows.filter((row) => row.status !== "synced");
  const lastPunch = rows.length > 0 ? rows[rows.length - 1] : null;
  const fieldNoteQueued = noteRows.filter((row) => row.status !== "synced");
  const failed = queued.filter((punch) => punch.status === "failed");
  const fieldNoteFailed = fieldNoteQueued.filter((n) => n.status === "failed");
  const withError =
    [...queued].reverse().find((punch) => punch.lastError) ||
    [...fieldNoteQueued].reverse().find((n) => n.lastError);

  const totalPending = queued.length + fieldNoteQueued.length;

  return {
    queued,
    lastPunch,
    fieldNoteQueued,
    pendingCount: totalPending,
    syncing:
      queued.some((punch) => punch.status === "syncing") ||
      fieldNoteQueued.some((n) => n.status === "syncing"),
    authRequired:
      queued.some((punch) => punch.status === "auth_required") ||
      fieldNoteQueued.some((n) => n.status === "auth_required"),
    failed,
    fieldNoteFailed,
    lastError: withError
      ? { status: withError.lastErrorStatus, message: withError.lastError ?? "" }
      : null,
    syncNow: () => requestSync({ force: true }),
    storageDegraded: false,
  };
}
